import { z } from 'zod';

// `state` stays a string: a state this frontend does not know must not fail the whole answer.
export const CompanyFeatureSchema = z.object({
  value: z.boolean().nullable(),
  state: z.string(),
});
export type CompanyFeature = z.infer<typeof CompanyFeatureSchema>;

// ERP type → whether the company may connect it; an ERP missing here may not be connected.
export const CompanyAvailableErpsSchema = z.record(z.string(), z.boolean());
export type CompanyAvailableErps = z.infer<typeof CompanyAvailableErpsSchema>;

export const CompanyDerivedFeaturesSchema = z.object({
  support_vendor_expenses: z.boolean(),
});

// Features this frontend gates access and behaviour on: the backend must send each of them, and a missing one fails
// here rather than reading as off. Removing one is a change on both sides.
const CompanyGatingFeaturesSchema = z.object({
  approval_stamp: CompanyFeatureSchema,
  approvals: CompanyFeatureSchema,
  approvals_form: CompanyFeatureSchema,
  enable_prepare_payment_for_non_published_invoices: CompanyFeatureSchema,
  erp_ssl_verification: CompanyFeatureSchema,
  fin: CompanyFeatureSchema,
  import_export_vendor_prefs_file: CompanyFeatureSchema,
  is_translate_enable: CompanyFeatureSchema,
  only_admin_can_change_af: CompanyFeatureSchema,
  paid_unpaid: CompanyFeatureSchema,
  polygon_highlighting: CompanyFeatureSchema,
  supplier_form: CompanyFeatureSchema,
  two_ways_matching: CompanyFeatureSchema,
  vendor_based_policy_management: CompanyFeatureSchema,
});

// The company's capabilities: its features, the ERPs it may connect, what the backend derives from it.
// Every key besides `erp` and `dynamic_features` is a feature.
export const CompanyCapabilitiesSchema = z
  .object({
    erp: CompanyAvailableErpsSchema,
    dynamic_features: CompanyDerivedFeaturesSchema,
    ...CompanyGatingFeaturesSchema.shape,
  })
  .catchall(CompanyFeatureSchema);
export type CompanyCapabilities = z.infer<typeof CompanyCapabilitiesSchema>;

// Feature key → its new value; the backend changes only the features named here.
export const CompanyFeatureValuesBodySchema = z.record(z.string(), z.boolean());
